import {
  type CopilotActionContext,
  type CopilotActionId,
} from './copilotActions.types';

/**
 * What is true of an editor, from which the copilot actions it offers are
 * derived. Editors declare their situation; they never state the answer.
 */
export type CopilotEditorSituation = {
  /** Writes an internal note rather than a customer-facing message. */
  isInternalNote?: boolean;
  /** A real conversation exists, so it has a language and can be drafted for. */
  hasConversation?: boolean;
  /** An inbound customer message exists to match tone against. */
  hasCustomerMessage?: boolean;
  /** Bound to one concrete channel, so a channel style guide can apply. */
  hasSpecificChannel?: boolean;
};

/**
 * The order is the order the toolbar renders them left to right, which also
 * decides which labels collapse first, so it is fixed here rather than left to
 * each editor.
 */
const deriveActions = (
  { hasConversation, hasSpecificChannel }: CopilotEditorSituation,
  isCustomerFacing: boolean,
): CopilotActionId[] => {
  // A writing style guide is configured per channel.
  const stylable = hasSpecificChannel && isCustomerFacing;
  // Both the conversation's language and its drafts come from a conversation.
  const conversational = hasConversation && isCustomerFacing;

  return [
    'improveReply',
    ...(stylable ? (['improveReplyMatchStyleGuide'] as const) : []),
    ...(conversational ? (['translateToConversationLanguage'] as const) : []),
    // Custom translate collects its own target language, so it needs nothing
    // from the situation and every editor offers it.
    'translate',
    'checkSpellingAndGrammar',
    'expandText',
    ...(conversational ? (['quickAiDraft', 'customAiDraft'] as const) : []),
  ];
};

/** What the editor is writing, for wording an action's name after it. */
const deriveNoun = ({
  isInternalNote,
  hasConversation,
}: CopilotEditorSituation): string => {
  if (isInternalNote) {
    return 'note';
  }

  if (hasConversation) {
    return 'reply';
  }

  return 'message';
};

/**
 * Matching the customer's tone needs a customer message to match. The toolbar
 * hides it; the dropdown panel shows it disabled, so both shapes are derived.
 */
const deriveImprovements = (
  { isInternalNote, hasCustomerMessage }: CopilotEditorSituation,
  isCustomerFacing: boolean,
): Pick<
  CopilotActionContext,
  'excludedImprovements' | 'disabledImprovements'
> => {
  if (hasCustomerMessage && isCustomerFacing) {
    return {};
  }

  return {
    excludedImprovements: ['match_tone'],
    disabledImprovements: [
      {
        value: 'match_tone',
        tooltip: isInternalNote
          ? 'Not available for internal notes'
          : 'Not available when composing a new message',
      },
    ],
  };
};

/** Applies the availability rules to a situation. */
export const deriveCopilotActionContext = (
  situation: CopilotEditorSituation,
): CopilotActionContext => {
  // Nothing aimed at the customer applies while writing an internal note.
  const isCustomerFacing = !situation.isInternalNote;
  const noun = deriveNoun(situation);

  return {
    actions: deriveActions(situation, isCustomerFacing),
    names: {
      improveReply: `Improve ${noun}`,
      expandText: `Expand ${noun}`,
    },
    ...deriveImprovements(situation, isCustomerFacing),
  };
};

/** The editors that offer copilot actions. */
export type CopilotEditor =
  | 'reply'
  | 'note'
  | 'composeEmail'
  | 'composeEmailNote'
  | 'composeSms'
  | 'composeSmsNote'
  | 'automationSideConversation'
  | 'automationSideConversationNote'
  | 'bulkNote';

const situations: Record<CopilotEditor, CopilotEditorSituation> = {
  /** Replying to a customer. The only editor where every action applies. */
  reply: {
    hasConversation: true,
    hasCustomerMessage: true,
    hasSpecificChannel: true,
  },

  /** Internal note on a conversation. */
  note: {
    isInternalNote: true,
    hasConversation: true,
    hasCustomerMessage: true,
    hasSpecificChannel: true,
  },

  /** A new outbound email, composed before any conversation exists. */
  composeEmail: { hasSpecificChannel: true },
  /** Internal note carried by a new outbound email. */
  composeEmailNote: { isInternalNote: true, hasSpecificChannel: true },

  /** A new outbound SMS, composed before any conversation exists. */
  composeSms: { hasSpecificChannel: true },
  /** Internal note carried by a new outbound SMS. */
  composeSmsNote: { isInternalNote: true, hasSpecificChannel: true },

  /** The automation node that starts a side conversation. */
  automationSideConversation: {},
  /** Internal note on that same automation node. */
  automationSideConversationNote: { isInternalNote: true },

  /** One note applied across many conversations, so likewise no single channel. */
  bulkNote: { isInternalNote: true },
};

/** Each editor's copilot config, derived from its situation. */
export const copilotActionContexts = Object.fromEntries(
  Object.entries(situations).map(([editor, situation]) => [
    editor,
    deriveCopilotActionContext(situation),
  ]),
) as Record<CopilotEditor, CopilotActionContext>;
